import { useEffect, useState, type CSSProperties } from 'react'

export type AnchorPlacement = 'top' | 'bottom' | 'left' | 'right'
export type AnchorPoint = 'start' | 'middle' | 'end'

type Options = {
  anchorSelector: string
  placement: AnchorPlacement
  point: AnchorPoint
  gap?: number
}

type Result = {
  style: CSSProperties
  ready: boolean
}

const DEFAULT_GAP = 16
const POLL_FRAME_BUDGET = 30 // ~500ms at 60fps

// Computes fixed-position coordinates for a popup placed adjacent to a DOM
// anchor selected by CSS selector. The TDS FeatureHighlight molecule has no
// built-in DOM anchoring -- its `pulsePoint`/`pulsePlacement` props only
// position the pulse decoration on the popup's own edge -- so this hook
// supplies the missing piece.
//
// Returns `ready: false` until the anchor is mounted, so callers can avoid
// flashing the popup at (0,0). Repositions on window resize and on size
// changes of the anchor itself (e.g. the sidebar's collapse animation).
export const useAnchoredPosition = ({
  anchorSelector,
  placement,
  point,
  gap = DEFAULT_GAP,
}: Options): Result => {
  const [style, setStyle] = useState<CSSProperties>({})
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let frameId = 0
    let pollAttempts = 0
    let resizeObserver: ResizeObserver | null = null
    let observedAnchor: Element | null = null

    const update = (anchor: Element) => {
      const rect = resolveRect(anchor)
      setStyle(computeStyle(rect, placement, point, gap))
      setReady(true)
    }

    const tryAttach = () => {
      const anchor = document.querySelector(anchorSelector)
      if (anchor) {
        observedAnchor = anchor
        update(anchor)
        if (typeof ResizeObserver !== 'undefined') {
          // Observe the rect-bearing element rather than the wrapper, which
          // may be `display: contents` and never fire resize events.
          const observed = findSizedDescendant(anchor) ?? anchor
          resizeObserver = new ResizeObserver(() => update(anchor))
          resizeObserver.observe(observed)
        }
        return
      }
      if (++pollAttempts < POLL_FRAME_BUDGET) {
        frameId = window.requestAnimationFrame(tryAttach)
      }
    }

    const onResize = () => {
      if (observedAnchor) update(observedAnchor)
    }

    tryAttach()
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      if (frameId) window.cancelAnimationFrame(frameId)
      if (resizeObserver) resizeObserver.disconnect()
    }
  }, [anchorSelector, placement, point, gap])

  return { style, ready }
}

// Anchor wrappers use `display: contents` to avoid breaking flex layout in
// the nav, which means the wrapper itself has no bounding rect. Descend
// until we find a child with a real box.
const findSizedDescendant = (el: Element): Element | null => {
  const rect = el.getBoundingClientRect()
  if (rect.width > 0 || rect.height > 0) return el
  for (let i = 0; i < el.children.length; i++) {
    const found = findSizedDescendant(el.children[i])
    if (found) return found
  }
  return null
}

const resolveRect = (el: Element): DOMRect => {
  const sized = findSizedDescendant(el)
  return (sized ?? el).getBoundingClientRect()
}

const computeStyle = (
  rect: DOMRect,
  placement: AnchorPlacement,
  point: AnchorPoint,
  gap: number
): CSSProperties => {
  // `point` describes where along the popup's edge the pulse sits AND where
  // along the anchor's edge that pulse should land — `start` lines the
  // popup's pulse-edge up with the anchor's start, `end` with the anchor's
  // end. The matching `translate(-N%)` then offsets the popup so that the
  // pulse-edge (rather than the popup's top-left) is the alignment point.
  const alignPct = point === 'start' ? 0 : point === 'middle' ? 50 : 100
  const alignFrac = alignPct / 100

  switch (placement) {
    case 'right':
      return {
        position: 'fixed',
        top: rect.top + rect.height * alignFrac,
        left: rect.right + gap,
        transform: `translate(0, -${alignPct}%)`,
      }
    case 'left':
      return {
        position: 'fixed',
        top: rect.top + rect.height * alignFrac,
        left: rect.left - gap,
        transform: `translate(-100%, -${alignPct}%)`,
      }
    case 'bottom':
      return {
        position: 'fixed',
        top: rect.bottom + gap,
        left: rect.left + rect.width * alignFrac,
        transform: `translate(-${alignPct}%, 0)`,
      }
    case 'top':
      return {
        position: 'fixed',
        top: rect.top - gap,
        left: rect.left + rect.width * alignFrac,
        transform: `translate(-${alignPct}%, -100%)`,
      }
  }
}
